import type { ColorValue } from "@-/com.npmjs.react-native";
import { createContext } from "react";

/** The light or dark appearance iOS gives a Liquid Glass surface for whatever is behind it. */
export type GlassAppearance = "dark" | "light";

/**
 * The appearance of the glass the content is drawn on (`GlassContentScope`), or `null` off glass
 * and until the glass reports. iOS flips glass between light and dark from its backdrop; `Text`
 * and `Icon` follow it by swapping the theme inks for the matching system ink, instead of
 * hard-coding light or dark ink per background.
 */
export const GlassContentContext = createContext<GlassAppearance | null>(null);

const FOREGROUND_INK_PATTERN = /(?:^|\s)text-foreground(?:\s|$)/u;
const MUTED_INK_PATTERN = /(?:^|\s)text-muted-foreground(?:\s|$)/u;

/** UIKit's `label` / `secondaryLabel` for each appearance. */
const GLASS_INK = {
  dark: { foreground: "#FFFFFF", muted: "rgba(235, 235, 245, 0.6)" },
  light: { foreground: "#000000", muted: "rgba(60, 60, 67, 0.6)" },
} as const satisfies Record<GlassAppearance, { foreground: string; muted: string }>;

/**
 * The ink for a glass-borne element whose classes resolve to a theme ink, or `undefined` to keep
 * its own colour (an accent, a destructive red, a disabled grey).
 */
export function getGlassInkColor(
  className: string | undefined,
  appearance: GlassAppearance,
): ColorValue | undefined {
  if (className === undefined) {
    return undefined;
  }
  if (MUTED_INK_PATTERN.test(className)) {
    return GLASS_INK[appearance].muted;
  }
  if (FOREGROUND_INK_PATTERN.test(className)) {
    return GLASS_INK[appearance].foreground;
  }
  return undefined;
}
